import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NODE_ENV } from '../../config';
import { getInspectorStyles } from './styles';

type InspectInfo = {
  selector: string;
  file?: string;
};

const isProd = (NODE_ENV || '').toLowerCase() === 'production';

export const DebugInspector: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [info, setInfo] = useState<InspectInfo | null>(null);
  const [copied, setCopied] = useState(false);
  const highlightRef = useRef<HTMLDivElement | null>(null);

  const toggle = useCallback(() => setEnabled((e) => !e), []);

  const onMove = useCallback((e: MouseEvent) => {
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    if (!el) return;
    // Ignore inspector UI itself
    if (isInspectorUI(el)) return;
    const rect = el.getBoundingClientRect();
    const hl = highlightRef.current!;
    hl.style.display = 'block';
    hl.style.left = `${rect.left + window.scrollX}px`;
    hl.style.top = `${rect.top + window.scrollY}px`;
    hl.style.width = `${rect.width}px`;
    hl.style.height = `${rect.height}px`;
    const selector = buildSelector(el);
    const file = el.getAttribute('data-debug-source') || undefined;
    setInfo({ selector, file });
  }, []);

  const onClick = useCallback((e: MouseEvent) => {
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    if (!el) return;
    // Allow clicks on inspector button itself
    if (isInspectorUI(el)) return;
    e.preventDefault();
    e.stopPropagation();
    const selector = buildSelector(el);
    const file = el.getAttribute('data-debug-source') || undefined;
    setInfo({ selector, file });
    if (file) {
      navigator.clipboard.writeText(file).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      });
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setInfo(null);
      if (highlightRef.current) highlightRef.current.style.display = 'none';
      // Remove global cursor override
      const styleTag = document.getElementById('debug-inspector-cursor');
      if (styleTag) styleTag.remove();
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      return;
    }
    // Add a global style to force crosshair cursor across the app, excluding inspector UI
    const style = document.createElement('style');
    style.id = 'debug-inspector-cursor';
    style.textContent = `
      html, body, * { cursor: crosshair !important; }
      [data-debug-ui="true"], [data-debug-ui="true"] * { cursor: auto !important; }
    `;
    document.head.appendChild(style);
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    return () => {
      const styleTag = document.getElementById('debug-inspector-cursor');
      if (styleTag) styleTag.remove();
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [enabled, onMove, onClick]);

  const styles = useMemo(() => getInspectorStyles(enabled), [enabled]);

  if (isProd) return null;

  return (
    <>
      <div ref={highlightRef} style={styles.highlight as React.CSSProperties} />
      <button
        type="button"
        onClick={toggle}
        style={styles.button as React.CSSProperties}
        aria-label="Toggle Debug Inspector"
        data-debug-ui="true"
      >
        {enabled ? 'Disable Inspector' : 'Enable Inspector'}
      </button>
      {(enabled || info) && (
        <div style={styles.panel as React.CSSProperties} data-debug-ui="true">
          {info && (
            <>
              <div>
                Target: <span style={styles.code}> {info.selector} </span>
              </div>
              <div>
                Source file: <span style={styles.file}>{info.file || 'unknown (auto-tag via dev Babel plugin)'}</span>
                {copied && <span style={styles.copied}>Copied!</span>}
              </div>
            </>
          )}
          <div style={{ fontSize: 11, color: '#bbb', marginTop: 4 }}>
            (Click any component to copy file:line to clipboard)
          </div>
        </div>
      )}
    </>
  );
};

function buildSelector(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const id = (el as HTMLElement).id ? `#${(el as HTMLElement).id}` : '';
  const cls = (el as HTMLElement).className
    ? '.' +
      String((el as HTMLElement).className)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .join('.')
    : '';
  return `${tag}${id}${cls}`;
}

export default DebugInspector;

function isInspectorUI(el: Element) {
  return !!el.closest('[data-debug-ui="true"]');
}
